import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react'
import { AccountAction, AppStateProvider, usePlan, useAppDispatch } from './store'
import { AuthProvider, useAuth } from './auth'
import AuthScreen from './components/AuthScreen'
import AccountMenu from './components/AccountMenu'
import { ConflictBar, SyncPill } from './components/SyncStatus'
import { LoadPhase, SyncedDocument, useSyncedDocument } from './lib/useSyncedDocument'
import { clearAllCaches } from './lib/localCache'
import { api } from './lib/api'
import Sidebar from './components/Sidebar'
import YearView from './components/YearView'
import MonthView from './components/MonthView'
import DayDetailModal from './components/DayDetailModal'
import RuleForm from './components/RuleForm'
import OneOffForm from './components/OneOffForm'
import AdjustmentForm from './components/AdjustmentForm'
import SettingsForm from './components/SettingsForm'
import AccountForm from './components/AccountForm'
import InterestForm from './components/InterestForm'
import ScopeSelect from './components/ScopeSelect'
import BudgetActualsModal from './components/BudgetActualsModal'
import TargetForm from './components/TargetForm'
import TransferForm from './components/TransferForm'
import DeleteAccountModal from './components/DeleteAccountModal'
import ImportStatementModal from './components/ImportStatementModal'
import CompareSpendingModal from './components/CompareSpendingModal'
import LowBalanceBanner from './components/LowBalanceBanner'
import { lowBalanceWarning, warningKey } from './lib/lowBalance'
import Tutorial from './components/Tutorial'
import ErrorBoundary from './components/ErrorBoundary'
import PrivacyPolicy from './components/PrivacyPolicy'
import TermsOfUse from './components/TermsOfUse'
import Splash, { SplashHold } from './components/Splash'
import InstallBanner from './components/InstallBanner'
import RemindersModal from './components/RemindersModal'
import CookieBanner from './components/CookieBanner'
import { dismissInstall, hasInstallBeenDismissed, promptInstall, useInstallMethod } from './lib/install'
import { inStoreApp, initReminders } from './lib/storeApp'
import { computeCashflow, targetAchievements } from './lib/cashflowEngine'
import { ALL_ACCOUNTS, Scope, combineResults, computeScope, includedAccounts } from './lib/accounts'
import { addDays, fromISODate, toISODate } from './lib/dateRules'
import { MONTH_NAMES } from './lib/format'
import {
  accountState,
  clearLegacyLocalData,
  defaultPlan,
  buildExport,
  exportAsJson,
  newBankAccount,
  planFromImport,
  readLegacyLocalData,
} from './lib/storage'
import { hasTutorialBeenDismissed, dismissTutorial, TUTORIAL_STEPS, TutorialView } from './lib/tutorial'
import {
  AppState,
  BankAccount,
  Plan,
  IncomeRule,
  ExpenseRule,
  BudgetItem,
  OneOffTransaction,
  BalanceAdjustment,
  BalanceTarget,
  SidebarTab,
  Transfer,
} from './types'

// The two screens that draw charts, and only those two, pull in the charting
// library — about half the bundle. Loading them on demand keeps that off the
// critical path for someone who has just opened the calendar on their phone.
const SandboxView = lazy(() => import('./components/SandboxView'))
const PayoffModal = lazy(() => import('./components/PayoffModal'))
const OverviewView = lazy(() => import('./components/OverviewView'))
// Only ever opened by an admin, so kept out of everyone else's download.
const AdminPanel = lazy(() => import('./components/AdminPanel'))

/** Shown while one of the lazy screens above is still on the wire. */
function ChartScreenFallback() {
  return <p className="text-sm text-ink/70 p-6">Loading…</p>
}

type ModalState =
  | { kind: 'income'; item?: IncomeRule }
  | { kind: 'expense'; item?: ExpenseRule }
  | { kind: 'budget'; item?: BudgetItem }
  | { kind: 'oneoff'; item?: OneOffTransaction; defaultDate?: string }
  | { kind: 'adjustment'; item?: BalanceAdjustment }
  | { kind: 'transfer'; item?: Transfer }
  | { kind: 'settings' }
  | { kind: 'payoff'; item: ExpenseRule }
  | { kind: 'actuals'; item: BudgetItem | ExpenseRule }
  | { kind: 'target'; item?: BalanceTarget }
  | { kind: 'account'; item?: BankAccount }
  | { kind: 'interest'; item: BankAccount }
  | { kind: 'deleteAccount' }
  | { kind: 'importStatement' }
  | { kind: 'compareSpending' }
  | { kind: 'reminders' }
  | null

type ViewMode = 'overview' | 'year' | 'month' | 'sandbox'

/**
 * Which account and which view were on screen last, so the app reopens where
 * it was left. Kept per browser rather than in the plan: switching isn't an
 * edit, and saving it to the server would turn looking at a different account
 * on a phone into a conflict with an edit on the laptop. Losing it just means
 * opening on the default.
 */
type Remembered = 'scope' | 'view'

function readRemembered(what: Remembered, userId: string): string | null {
  try {
    return localStorage.getItem(`snapshot-money:${what}:${userId}`)
  } catch {
    return null
  }
}

function remember(what: Remembered, userId: string, value: string) {
  try {
    localStorage.setItem(`snapshot-money:${what}:${userId}`, value)
  } catch {
    // Nothing to do: next time opens on the default.
  }
}

const VIEW_MODES: ViewMode[] = ['overview', 'year', 'month', 'sandbox']

/**
 * Resolves a remembered or chosen scope against the plan as it is now. An id
 * that no longer exists — deleted here, or on another device — falls back to
 * every account, or to the only one.
 */
function resolveScope(plan: Plan, value: string | null): Scope {
  const account = plan.accounts.find((a) => a.id === value)
  if (account) return { kind: 'account', account }
  if (plan.accounts.length > 1) return { kind: 'all' }
  return { kind: 'account', account: plan.accounts[0] }
}

/**
 * Rendered twice with different classes — inline in the header from sm up, and
 * on its own row below it on a phone. Only ever one of them is displayed, and
 * the tutorial picks whichever that is.
 */
function ViewSwitcher({
  viewMode,
  onSelect,
  className,
}: {
  viewMode: ViewMode
  onSelect: (mode: ViewMode) => void
  className: string
}) {
  return (
    <div data-tour="view-switcher" className={`rounded-sm border border-line overflow-hidden ${className}`}>
      {(['overview', 'year', 'month', 'sandbox'] as const).map((mode, i) => (
        <button
          key={mode}
          onClick={() => onSelect(mode)}
          aria-current={viewMode === mode ? 'page' : undefined}
          className={`flex-1 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm capitalize ${
            i > 0 ? 'border-l border-line' : ''
          } ${viewMode === mode ? 'bg-accent text-white' : 'bg-white text-ink/80 hover:bg-paper'}`}
        >
          {mode}
        </button>
      ))}
    </div>
  )
}

function AppContent({ sync }: { sync: SyncedDocument }) {
  const plan = usePlan()
  const dispatch = useAppDispatch()
  const { user, signOut, refresh, deleteAccount } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const today = new Date()
  const todayIso = toISODate(today)

  // A save from another tab or device, already agreed with the server: put it
  // on screen. It's the very object the sync hook recorded as saved, so the
  // store handing it back doesn't send it straight back to the server.
  const [updatedElsewhere, setUpdatedElsewhere] = useState(false)
  useEffect(() => {
    if (!sync.remote) return
    dispatch({ type: 'REPLACE_STATE', payload: sync.remote.plan })
    setUpdatedElsewhere(true)
    const hide = setTimeout(() => setUpdatedElsewhere(false), 4000)
    return () => clearTimeout(hide)
  }, [sync.remote, dispatch])

  const [scopeValue, setScopeValue] = useState<string | null>(() => (user ? readRemembered('scope', user.id) : null))
  const scope = resolveScope(plan, scopeValue)
  /** The account in view, as the engine and the per-account screens see it. Null on the combined view. */
  const state: AppState | null = scope.kind === 'account' ? accountState(plan, scope.account) : null
  const activeAccountId = scope.kind === 'account' ? scope.account.id : ALL_ACCOUNTS
  const currencySymbol = plan.settings.currencySymbol

  function selectScope(value: string) {
    setScopeValue(value)
    if (user) remember('scope', user.id, value)
  }

  /** Sends an edit to the account in view. Every list screen is per-account, so there always is one. */
  function act(action: AccountAction, accountId = activeAccountId) {
    if (accountId === ALL_ACCOUNTS) return
    dispatch({ type: 'ACCOUNT', accountId, action })
  }

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = user ? readRemembered('view', user.id) : null
    if (saved && (VIEW_MODES as string[]).includes(saved)) return saved as ViewMode
    return plan.accounts.length > 1 ? 'overview' : 'month'
  })
  useEffect(() => {
    if (user) remember('view', user.id, viewMode)
  }, [user, viewMode])
  const [selectedYear, setSelectedYear] = useState(today.getFullYear())
  const [selectedMonth0, setSelectedMonth0] = useState(today.getMonth())
  const [dayDetailIso, setDayDetailIso] = useState<string | null>(null)
  const [modal, setModal] = useState<ModalState>(null)
  const [formKey, setFormKey] = useState(0)
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('income')
  const [strictMode, setStrictMode] = useState(false)
  const [showTutorial, setShowTutorial] = useState(() => !hasTutorialBeenDismissed())
  /** The sidebar is a permanent column from md up and a drawer below it. */
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const installMethod = useInstallMethod()
  /** Dismissing the banner is remembered; the account menu can still bring it back. */
  const [installHidden, setInstallHidden] = useState(hasInstallBeenDismissed)

  /**
   * A form opened from the drawer would otherwise appear behind it. Closing on
   * any modal keeps the two from stacking on a phone-sized screen.
   */
  useEffect(() => {
    if (modal || dayDetailIso) setSidebarOpen(false)
  }, [modal, dayDetailIso])

  /**
   * Landing back here from a confirmation link. The session already knows the
   * outcome, so this only has to say so and tidy the address bar.
   */
  const [verifyNotice, setVerifyNotice] = useState<string | null>(null)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const verify = params.get('verify')
    if (!verify) return
    setVerifyNotice(
      {
        ok: 'Email address confirmed.',
        already: 'That address was already confirmed.',
        expired: 'That confirmation link had expired — send yourself another from the account menu.',
        invalid: "That confirmation link didn't look right — send yourself another from the account menu.",
      }[verify] ?? null,
    )
    window.history.replaceState({}, '', window.location.pathname)
    if (verify === 'ok') void refresh()
  }, [refresh])

  /**
   * Inside the store apps: the first sign-in turns the reminder notifications
   * on, and tapping the monthly one lands on `?open=import-statement`.
   */
  useEffect(() => {
    if (!user) return
    initReminders()
    const params = new URLSearchParams(window.location.search)
    if (params.get('open') !== 'import-statement') return
    setModal({ kind: 'importStatement' })
    window.history.replaceState({}, '', window.location.pathname)
  }, [user])

  /** Re-opens a blank "add" modal of the same kind, forcing the form to remount fresh. */
  function reopenBlank(kind: 'income' | 'expense' | 'budget' | 'oneoff' | 'transfer' | 'adjustment' | 'target') {
    setFormKey((k) => k + 1)
    setModal({ kind } as ModalState)
  }

  function dismissInstallBanner() {
    dismissInstall()
    setInstallHidden(true)
  }

  /** The browser's own prompt where there is one; otherwise the banner's instructions. */
  function handleInstall() {
    if (installMethod === 'prompt') void promptInstall()
    else setInstallHidden(false)
  }

  // The tour walks through an account's lists, which only exist with one in
  // view — so a tour started on the combined view moves to the first account.
  useEffect(() => {
    if (showTutorial && scope.kind === 'all') selectScope(plan.accounts[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTutorial])

  function finishTutorial() {
    dismissTutorial()
    setShowTutorial(false)
  }

  const results = useMemo(() => {
    const start = new Date(selectedYear, 0, 1)
    const end = new Date(selectedYear, 11, 31)
    const list = computeScope(plan, scope, start, end, !strictMode)
    return new Map(list.map((r) => [r.date, r]))
    // scope is derived from plan and scopeValue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, scopeValue, selectedYear, strictMode])

  // Targets can be years out, so this looks further ahead than the year on
  // screen rather than reusing `results`. They belong to one account, so the
  // combined view has none.
  const achievements = useMemo(() => {
    if (!state) return new Map()
    const from = fromISODate(state.settings.startingBalanceDate)
    return targetAchievements(state, from, addDays(today, 365 * 3))
    // state is derived from plan and scopeValue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, scopeValue, todayIso])

  // Each account's figure for today, for the account list on the combined view.
  const { todayByAccount, combinedToday } = useMemo(() => {
    // Midnight, not `today`: the engine's days are dates, and one carrying the
    // time of day would filter today itself out.
    const day = fromISODate(todayIso)
    const byAccount = new Map(
      plan.accounts.map((a) => [a.id, computeCashflow(accountState(plan, a), day, day, !strictMode)[0]]),
    )
    const [combined] = combineResults(
      includedAccounts(plan).map((a) => ({
        name: a.name,
        results: byAccount.get(a.id) ? [byAccount.get(a.id)!] : [],
      })),
      plan.settings.combinedRagThresholds,
    )
    return { todayByAccount: byAccount, combinedToday: combined }
  }, [plan, todayIso, strictMode])

  // Whether today is on screen. The sandbox has no date navigation, so there's
  // never anywhere to jump back to from there.
  const showingNow =
    viewMode === 'sandbox' ||
    viewMode === 'overview' ||
    (selectedYear === today.getFullYear() &&
      (viewMode === 'year' || selectedMonth0 === today.getMonth()))

  /** Back to today, staying in whichever view you're using. */
  function jumpToNow() {
    setSelectedYear(today.getFullYear())
    setSelectedMonth0(today.getMonth())
  }

  // The warning is about the plan, so it follows every edit; the dismissal is
  // about one particular low point, kept per browser like the remembered view.
  const warning = useMemo(() => lowBalanceWarning(plan, today), [plan, todayIso]) // eslint-disable-line react-hooks/exhaustive-deps
  const dismissKey = user ? `snapshot-money:low-balance-dismissed:${user.id}` : null
  const [dismissedWarning, setDismissedWarning] = useState<string | null>(() => {
    try {
      return dismissKey ? localStorage.getItem(dismissKey) : null
    } catch {
      return null
    }
  })
  function dismissWarning() {
    if (!warning || !dismissKey) return
    const key = warningKey(warning)
    setDismissedWarning(key)
    try {
      localStorage.setItem(dismissKey, key)
    } catch {
      // It comes back on the next load; nothing worse.
    }
  }

  /** Opens the calendar on a date and shows its breakdown. */
  function jumpToDate(iso: string) {
    const d = fromISODate(iso)
    setSelectedYear(d.getFullYear())
    setSelectedMonth0(d.getMonth())
    setViewMode('month')
    setModal(null)
    setDayDetailIso(iso)
  }

  function goYear(delta: number) {
    setSelectedYear((y) => y + delta)
  }

  function goMonth(delta: number) {
    let m = selectedMonth0 + delta
    let y = selectedYear
    if (m > 11) {
      m = 0
      y++
    } else if (m < 0) {
      m = 11
      y--
    }
    setSelectedMonth0(m)
    setSelectedYear(y)
  }

  async function handleSignOut() {
    // Push anything still in the debounce window before the session ends,
    // otherwise signing out is a way to lose the last few seconds of edits.
    sync.flushNow()
    // Nothing of this account's plan should be left readable on a shared or
    // borrowed device.
    clearAllCaches()
    await signOut()
  }

  async function handleExport() {
    if (!user) return
    // The plan comes from the screen rather than the server, so edits still
    // waiting to save are in it. The sandbox has to be fetched; offline, the
    // file goes without it rather than not at all.
    const sandbox = await api.getState('sandbox').then(
      (doc) => doc.data,
      () => null,
    )
    const blob = new Blob([exportAsJson(buildExport(user, plan, sandbox))], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `snapshot-money-export-${todayIso}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string)
        // A full export or a bare plan; one from before multiple accounts is
        // upgraded on the way in.
        dispatch({ type: 'REPLACE_STATE', payload: planFromImport(parsed) })
      } catch {
        alert('That file could not be read as a valid export.')
      }
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  return (
    // 100dvh rather than 100vh: on iOS Safari the address bar is included in
    // vh, so the last row of the calendar sat under it.
    <div className="flex h-[100dvh] overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-ink/40 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}

      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        plan={plan}
        state={state}
        scopeValue={activeAccountId}
        onChangeScope={selectScope}
        onAddAccount={() => setModal({ kind: 'account' })}
        onImportStatement={() => setModal({ kind: 'importStatement' })}
        onEditAccount={() => scope.kind === 'account' && setModal({ kind: 'account', item: scope.account })}
        onEditInterest={() => scope.kind === 'account' && setModal({ kind: 'interest', item: scope.account })}
        todayByAccount={todayByAccount}
        combinedToday={combinedToday}
        activeTab={sidebarTab}
        onSelectTab={setSidebarTab}
        onAddIncome={() => setModal({ kind: 'income' })}
        onEditIncome={(item) => setModal({ kind: 'income', item })}
        onAddExpense={() => setModal({ kind: 'expense' })}
        onEditExpense={(item) => setModal({ kind: 'expense', item })}
        onAddBudget={() => setModal({ kind: 'budget' })}
        onEditBudget={(item) => setModal({ kind: 'budget', item })}
        onAddOneOff={() => setModal({ kind: 'oneoff' })}
        onEditOneOff={(item) => setModal({ kind: 'oneoff', item })}
        onAddTransfer={() => setModal({ kind: 'transfer' })}
        onEditTransfer={(item) => {
          // The sidebar hands over this account's side of it; the form edits the transfer itself.
          const transfer = plan.transfers.find((t) => t.id === item.id)
          if (transfer) setModal({ kind: 'transfer', item: transfer })
        }}
        onAddAdjustment={() => setModal({ kind: 'adjustment' })}
        onEditAdjustment={(item) => setModal({ kind: 'adjustment', item })}
        onOpenSettings={() => setModal({ kind: 'settings' })}
        onOpenPayoff={(item) => setModal({ kind: 'payoff', item })}
        onOpenActuals={(item) => setModal({ kind: 'actuals', item })}
        onAddTarget={() => setModal({ kind: 'target' })}
        onEditTarget={(item) => setModal({ kind: 'target', item })}
        achievements={achievements}
        todayIso={todayIso}
        strictMode={strictMode}
        onToggleStrictMode={setStrictMode}
      />

      <main className="flex-1 overflow-y-auto">
        <div className="sticky top-0 bg-paper/95 backdrop-blur border-b border-line z-20">
          <div className="px-3 sm:px-6 py-2.5 sm:py-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                aria-label="Show income, outgoings and budgets"
                className="md:hidden shrink-0 w-9 h-9 flex items-center justify-center rounded-sm border border-line bg-white text-ink/80"
              >
                <span className="block w-4 border-t border-current relative before:absolute before:-top-1.5 before:left-0 before:w-4 before:border-t before:border-current after:absolute after:top-1.5 after:left-0 after:w-4 after:border-t after:border-current" />
              </button>

              <ViewSwitcher
                viewMode={viewMode}
                onSelect={setViewMode}
                className="hidden sm:flex shrink-0"
              />

              {viewMode === 'year' ? (
                <div className="flex items-center shrink-0">
                  <button onClick={() => goYear(-1)} aria-label="Previous year" className="px-2 py-1 text-ink/75 hover:text-ink">
                    ‹
                  </button>
                  <span className="font-display text-base sm:text-lg text-ink w-14 sm:w-16 text-center">{selectedYear}</span>
                  <button onClick={() => goYear(1)} aria-label="Next year" className="px-2 py-1 text-ink/75 hover:text-ink">
                    ›
                  </button>
                </div>
              ) : viewMode === 'month' ? (
                <div className="flex items-center min-w-0">
                  <button onClick={() => goMonth(-1)} aria-label="Previous month" className="px-2 py-1 text-ink/75 hover:text-ink">
                    ‹
                  </button>
                  <span className="font-display text-base sm:text-lg text-ink w-24 sm:w-40 text-center truncate">
                    {/* "Sep 2026" on a phone, "September 2026" once there's room. */}
                    <span className="sm:hidden">{MONTH_NAMES[selectedMonth0].slice(0, 3)}</span>
                    <span className="hidden sm:inline">{MONTH_NAMES[selectedMonth0]}</span> {selectedYear}
                  </span>
                  <button onClick={() => goMonth(1)} aria-label="Next month" className="px-2 py-1 text-ink/75 hover:text-ink">
                    ›
                  </button>
                </div>
              ) : (
                <span className="font-display text-base sm:text-lg text-ink capitalize">{viewMode}</span>
              )}

              {!showingNow && (
                <button
                  onClick={jumpToNow}
                  title={`Back to ${MONTH_NAMES[today.getMonth()]} ${today.getFullYear()}`}
                  className="px-2 py-1 text-xs rounded-sm border border-line text-accent hover:bg-paper whitespace-nowrap shrink-0"
                >
                  Now
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <SyncPill status={sync.status} onRetry={sync.flushNow} />
              {user && (
                <AccountMenu
                  user={user}
                  onSignOut={handleSignOut}
                  onHelp={() => setShowTutorial(true)}
                  onExport={handleExport}
                  onImport={() => fileInputRef.current?.click()}
                  onInstall={installMethod ? handleInstall : undefined}
                  onReminders={inStoreApp() ? () => setModal({ kind: 'reminders' }) : undefined}
                  onDeleteAccount={() => setModal({ kind: 'deleteAccount' })}
                  onImportStatement={() => setModal({ kind: 'importStatement' })}
                  onCompareSpending={() => setModal({ kind: 'compareSpending' })}
                />
              )}
              <input ref={fileInputRef} type="file" accept="application/json" className="hidden" onChange={handleImportFile} />
            </div>
          </div>

          {/* On a phone the switcher gets its own full-width row: four labels,
              a month name and two menus don't fit across 375px. Below md the
              sidebar is a drawer, so the account picker joins it here too. */}
          {/* Stacked on a phone: side by side, the four view labels don't fit
              next to the picker. */}
          <div className="flex flex-col md:hidden gap-2 mx-3 mb-2.5">
            <ScopeSelect
              plan={plan}
              value={activeAccountId}
              onChange={selectScope}
              onAddAccount={() => setModal({ kind: 'account' })}
              className="w-full sm:w-56"
            />
            <ViewSwitcher viewMode={viewMode} onSelect={setViewMode} className="flex sm:hidden" />
          </div>

          <ConflictBar
            status={sync.status}
            onKeepMine={sync.keepMine}
            onTakeTheirs={() => {
              const adopted = sync.takeTheirs()
              if (adopted) dispatch({ type: 'REPLACE_STATE', payload: adopted })
            }}
          />

          {warning && warningKey(warning) !== dismissedWarning && (
            <LowBalanceBanner
              warning={warning}
              currencySymbol={plan.settings.currencySymbol}
              todayIso={todayIso}
              onShow={() => {
                if (scope.kind === 'account') selectScope(ALL_ACCOUNTS)
                jumpToDate(warning.date)
              }}
              onDismiss={dismissWarning}
            />
          )}

          {/* Held back during the tour, which has enough to say on a first visit. */}
          {installMethod && !installHidden && !showTutorial && (
            <InstallBanner method={installMethod} onInstall={handleInstall} onDismiss={dismissInstallBanner} />
          )}

          {updatedElsewhere && (
            <div role="status" className="px-4 sm:px-6 py-2 bg-accent-soft border-b border-accent/20">
              <p className="text-sm text-accent">Updated with changes from another device.</p>
            </div>
          )}

          {verifyNotice && (
            <div className="px-4 sm:px-6 py-2 bg-accent-soft border-b border-accent/20 flex items-center justify-between gap-3">
              <p className="text-sm text-accent">{verifyNotice}</p>
              <button onClick={() => setVerifyNotice(null)} aria-label="Dismiss" className="text-accent hover:text-ink">
                ×
              </button>
            </div>
          )}
        </div>

        <div data-tour="calendar" className="p-3 sm:p-6">
          <ErrorBoundary
            label={viewMode === 'sandbox' ? 'The sandbox' : viewMode === 'overview' ? 'The overview' : 'The calendar'}
            resetKey={`${viewMode}:${activeAccountId}`}
          >
          {viewMode === 'overview' ? (
            <Suspense fallback={<ChartScreenFallback />}>
              <OverviewView
                plan={plan}
                scope={scope}
                todayIso={todayIso}
                includeAdjustments={!strictMode}
                onSelectAccount={selectScope}
                onAddAccount={() => setModal({ kind: 'account' })}
                onJumpToDate={jumpToDate}
              />
            </Suspense>
          ) : viewMode === 'year' ? (
            <YearView
              year={selectedYear}
              results={results}
              currencySymbol={currencySymbol}
              todayIso={todayIso}
              onSelectMonth={(m) => {
                setSelectedMonth0(m)
                setViewMode('month')
              }}
              onSelectDay={setDayDetailIso}
            />
          ) : viewMode === 'month' ? (
            <MonthView
              year={selectedYear}
              month0={selectedMonth0}
              results={results}
              currencySymbol={currencySymbol}
              todayIso={todayIso}
              onSelectDay={setDayDetailIso}
            />
          ) : (
            <Suspense fallback={<ChartScreenFallback />}>
              <SandboxView basePlan={plan} scope={scope} />
            </Suspense>
          )}
          </ErrorBoundary>
        </div>
      </main>

      {/* One modal shows at a time, so a single boundary covers them all;
          closing the modal is the way out if one of them breaks. */}
      <ErrorBoundary
        label="That dialog"
        variant="overlay"
        resetKey={`${modal?.kind ?? 'none'}:${dayDetailIso ?? ''}`}
        onReset={() => {
          setModal(null)
          setDayDetailIso(null)
        }}
      >
      {dayDetailIso && (
        <DayDetailModal
          iso={dayDetailIso}
          result={results.get(dayDetailIso)}
          currencySymbol={currencySymbol}
          onClose={() => setDayDetailIso(null)}
          onAddOneOff={(iso) => {
            setDayDetailIso(null)
            setModal({ kind: 'oneoff', defaultDate: iso })
          }}
        />
      )}

      {modal?.kind === 'income' && state && (
        <RuleForm
          key={formKey}
          kind="income"
          initial={modal.item}
          onClose={() => setModal(null)}
          onSave={(item, addAnother) => {
            if (modal.item) act({ type: 'UPDATE_INCOME', payload: item as IncomeRule })
            else act({ type: 'ADD_INCOME', payload: item as IncomeRule })
            setSidebarTab('income')
            if (addAnother) reopenBlank('income')
            else setModal(null)
          }}
          onDelete={
            modal.item
              ? () => {
                  act({ type: 'DELETE_INCOME', payload: modal.item!.id })
                  setModal(null)
                }
              : undefined
          }
        />
      )}

      {modal?.kind === 'expense' && state && (
        <RuleForm
          key={formKey}
          kind="expense"
          initial={modal.item}
          onClose={() => setModal(null)}
          onSave={(item, addAnother) => {
            if (modal.item) act({ type: 'UPDATE_EXPENSE', payload: item as ExpenseRule })
            else act({ type: 'ADD_EXPENSE', payload: item as ExpenseRule })
            setSidebarTab('expenses')
            if (addAnother) reopenBlank('expense')
            else setModal(null)
          }}
          onDelete={
            modal.item
              ? () => {
                  act({ type: 'DELETE_EXPENSE', payload: modal.item!.id })
                  setModal(null)
                }
              : undefined
          }
        />
      )}

      {modal?.kind === 'budget' && state && (
        <RuleForm
          key={formKey}
          kind="budget"
          initial={modal.item}
          onClose={() => setModal(null)}
          onSave={(item, addAnother) => {
            if (modal.item) act({ type: 'UPDATE_BUDGET', payload: item as BudgetItem })
            else act({ type: 'ADD_BUDGET', payload: item as BudgetItem })
            setSidebarTab('budgets')
            if (addAnother) reopenBlank('budget')
            else setModal(null)
          }}
          onDelete={
            modal.item
              ? () => {
                  act({ type: 'DELETE_BUDGET', payload: modal.item!.id })
                  setModal(null)
                }
              : undefined
          }
        />
      )}

      {modal?.kind === 'oneoff' && (
        <OneOffForm
          key={formKey}
          initial={modal.item}
          defaultDate={modal.defaultDate}
          accountChoices={scope.kind === 'all' && !modal.item ? plan.accounts : undefined}
          onClose={() => setModal(null)}
          onSave={(item, addAnother, accountId) => {
            if (modal.item) act({ type: 'UPDATE_ONEOFF', payload: item })
            else act({ type: 'ADD_ONEOFF', payload: item }, accountId ?? activeAccountId)
            setSidebarTab('oneoffs')
            if (addAnother) reopenBlank('oneoff')
            else setModal(null)
          }}
          onDelete={
            modal.item
              ? () => {
                  act({ type: 'DELETE_ONEOFF', payload: modal.item!.id })
                  setModal(null)
                }
              : undefined
          }
        />
      )}

      {modal?.kind === 'importStatement' && (
        <ImportStatementModal
          plan={plan}
          defaultAccountId={scope.kind === 'account' ? scope.account.id : plan.accounts[0].id}
          onClose={() => setModal(null)}
          onApply={(accountId, { oneOffs, actuals, budgets, expenses, incomes, newIncomes, newExpenses, replaceExamples, adjustment, shopSpending, statementFingerprint }) => {
            act(
              {
                type: 'APPLY_IMPORT',
                payload: { oneOffs, actuals, budgets, expenses, incomes, newIncomes, newExpenses, replaceExamples, adjustment, shopSpending, statementFingerprint },
              },
              accountId,
            )
            // Starting a plan from a statement: show the pay and bills it found.
            setSidebarTab(oneOffs.length === 0 && (newIncomes?.length || newExpenses?.length) ? 'income' : 'oneoffs')
            setModal(null)
          }}
        />
      )}

      {modal?.kind === 'reminders' && <RemindersModal onClose={() => setModal(null)} />}

      {modal?.kind === 'compareSpending' && <CompareSpendingModal plan={plan} onClose={() => setModal(null)} />}

      {modal?.kind === 'deleteAccount' && user && (
        <DeleteAccountModal
          email={user.email}
          onDelete={deleteAccount}
          onExport={() => void handleExport()}
          onClose={() => setModal(null)}
        />
      )}

      {modal?.kind === 'transfer' && (
        <TransferForm
          key={formKey}
          initial={modal.item}
          accounts={plan.accounts}
          defaultFromAccountId={scope.kind === 'account' ? scope.account.id : undefined}
          onClose={() => setModal(null)}
          onSave={(item, addAnother) => {
            dispatch({ type: modal.item ? 'UPDATE_TRANSFER' : 'ADD_TRANSFER', payload: item })
            setSidebarTab('transfers')
            if (addAnother) reopenBlank('transfer')
            else setModal(null)
          }}
          onDelete={
            modal.item
              ? () => {
                  dispatch({ type: 'DELETE_TRANSFER', payload: modal.item!.id })
                  setModal(null)
                }
              : undefined
          }
        />
      )}

      {modal?.kind === 'adjustment' && state && (
        <AdjustmentForm
          key={formKey}
          initial={modal.item}
          onClose={() => setModal(null)}
          onSave={(item, addAnother) => {
            if (modal.item) act({ type: 'UPDATE_ADJUSTMENT', payload: item })
            else act({ type: 'ADD_ADJUSTMENT', payload: item })
            setSidebarTab('adjustments')
            if (addAnother) reopenBlank('adjustment')
            else setModal(null)
          }}
          onDelete={
            modal.item
              ? () => {
                  act({ type: 'DELETE_ADJUSTMENT', payload: modal.item!.id })
                  setModal(null)
                }
              : undefined
          }
        />
      )}

      {modal?.kind === 'settings' && (
        <SettingsForm
          settings={plan.settings}
          showCombined={plan.accounts.length > 1}
          onClose={() => setModal(null)}
          onSave={(s) => {
            dispatch({ type: 'SET_PLAN_SETTINGS', payload: s })
            setModal(null)
          }}
        />
      )}

      {modal?.kind === 'interest' && (
        <InterestForm
          account={modal.item}
          currencySymbol={currencySymbol}
          onClose={() => setModal(null)}
          onSave={(interest) => {
            dispatch({ type: 'UPDATE_ACCOUNT', payload: { id: modal.item.id, interest } })
            setModal(null)
          }}
        />
      )}

      {modal?.kind === 'account' && (
        <AccountForm
          initial={modal.item}
          onClose={() => setModal(null)}
          onSave={(values) => {
            if (modal.item) {
              dispatch({ type: 'UPDATE_ACCOUNT', payload: { id: modal.item.id, ...values } })
            } else {
              const account = { ...newBankAccount(values.name, values.settings), includeInTotal: values.includeInTotal }
              dispatch({ type: 'ADD_ACCOUNT', payload: account })
              selectScope(account.id)
            }
            setModal(null)
          }}
          onDelete={
            modal.item && plan.accounts.length > 1
              ? () => {
                  dispatch({ type: 'DELETE_ACCOUNT', payload: modal.item!.id })
                  selectScope(ALL_ACCOUNTS)
                  setModal(null)
                }
              : undefined
          }
        />
      )}

      {modal?.kind === 'payoff' && state && (
        <Suspense fallback={<ChartScreenFallback />}>
          <PayoffModal
            expense={modal.item}
            currencySymbol={currencySymbol}
            onClose={() => setModal(null)}
            onApplyEndDate={(iso) => {
              act({ type: 'UPDATE_EXPENSE', payload: { ...modal.item, endDate: iso } })
              setModal(null)
            }}
          />
        </Suspense>
      )}

      {modal?.kind === 'target' && state && (
        <TargetForm
          key={formKey}
          initial={modal.item}
          achievements={modal.item ? achievements.get(modal.item.id) : undefined}
          currencySymbol={currencySymbol}
          todayIso={todayIso}
          onJumpToDate={jumpToDate}
          onClose={() => setModal(null)}
          onSave={(item, addAnother) => {
            if (modal.item) act({ type: 'UPDATE_TARGET', payload: item })
            else act({ type: 'ADD_TARGET', payload: item })
            setSidebarTab('targets')
            if (addAnother) reopenBlank('target')
            else setModal(null)
          }}
          onDelete={
            modal.item
              ? () => {
                  act({ type: 'DELETE_TARGET', payload: modal.item!.id })
                  setModal(null)
                }
              : undefined
          }
        />
      )}

      {modal?.kind === 'actuals' && state && (
        <BudgetActualsModal
          budget={modal.item}
          state={state!}
          year={selectedYear}
          currencySymbol={currencySymbol}
          onSetActual={(entry) => act({ type: 'SET_ACTUAL', payload: entry })}
          onClearActual={(sourceId, date) => act({ type: 'CLEAR_ACTUAL', payload: { sourceId, date } })}
          onClose={() => setModal(null)}
        />
      )}
      </ErrorBoundary>

      {showTutorial && (
        <Tutorial
          steps={TUTORIAL_STEPS}
          currentView={viewMode}
          onSetView={(v: TutorialView) => setViewMode(v)}
          currentTab={sidebarTab}
          onSetTab={setSidebarTab}
          sidebarOpen={sidebarOpen}
          onSetSidebarOpen={setSidebarOpen}
          onFinish={finishTutorial}
        />
      )}
    </div>
  )
}

/** Full-screen message, for the handful of states that have nothing else to show. */
function Notice({
  title,
  children,
  action,
}: {
  title: string
  children?: React.ReactNode
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-4">
      <div className="max-w-sm text-center">
        <h1 className="font-display text-2xl text-ink">{title}</h1>
        {children && <div className="text-sm text-ink/75 mt-3">{children}</div>}
        {action && (
          <button
            onClick={action.onClick}
            className="mt-6 px-4 py-2 text-sm rounded-sm bg-accent text-white hover:brightness-110"
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Loads the signed-in user's plan, then hands it to the store.
 *
 * Nothing below this point knows the state came from a server: the store gets
 * a starting value and a callback, exactly as it once got localStorage.
 */
function SignedInApp() {
  const { user } = useAuth()
  const doc = useSyncedDocument('main', user!.id, { live: true })
  /**
   * The plan the store opens on, and the load it was decided from. A reload
   * (the "Try again" button, or a hot reload in development) fetches the
   * server's copy afresh, and the store must reopen on *that* — reopening on
   * the plan from an earlier load and then saving it would write stale data
   * over newer, at a version the server has no reason to refuse.
   */
  const [opening, setOpening] = useState<{ load: LoadPhase; plan: Plan } | null>(null)
  const current = opening && opening.load === doc.load ? opening.plan : null
  const [offerImport, setOfferImport] = useState(false)

  // A plan that predates accounts may be sitting in this browser's
  // localStorage. Offer to carry it over rather than starting the person from
  // scratch — but only into an empty account, where nothing can be overwritten.
  const legacy = useMemo(() => readLegacyLocalData(), [])

  useEffect(() => {
    if (doc.load.phase !== 'ready' || opening?.load === doc.load) return

    if (doc.load.initial) {
      setOpening({ load: doc.load, plan: doc.load.initial })
      return
    }

    if (legacy.main) {
      setOfferImport(true)
      return
    }

    // A brand new account. Save the starter plan straight away rather than
    // waiting for a first edit, so that looking around and closing the tab
    // still leaves an account with something in it.
    const fresh = defaultPlan()
    doc.push(fresh)
    setOpening({ load: doc.load, plan: fresh })
  }, [doc, doc.load, legacy.main, opening])

  if (doc.load.phase === 'loading') return <Splash label="Loading your plan…" />

  if (doc.load.phase === 'needs-verification') {
    return (
      <Notice title="Confirm your email address">
        <p>{doc.load.message}</p>
        <p className="mt-2">
          The link was sent when you signed up. Check the inbox for the address you used, then reload
          this page.
        </p>
      </Notice>
    )
  }

  if (doc.load.phase === 'unavailable') {
    return (
      <Notice title="Couldn't load your plan" action={{ label: 'Try again', onClick: doc.reload }}>
        <p>{doc.load.message}</p>
      </Notice>
    )
  }

  if (offerImport && legacy.main) {
    return (
      <Notice
        title="Bring your plan across?"
        action={{
          label: 'Import it',
          onClick: () => {
            doc.push(legacy.main!)
            clearLegacyLocalData()
            setOpening({ load: doc.load, plan: legacy.main! })
            setOfferImport(false)
          },
        }}
      >
        <p>
          This browser has a plan saved from before the app had accounts. Your account is empty, so it
          can be moved over as it stands.
        </p>
        <button
          onClick={() => {
            // Leave the old data where it is: declining here shouldn't destroy
            // it, and the offer can then be taken up from another device.
            const fresh = defaultPlan()
            doc.push(fresh)
            setOpening({ load: doc.load, plan: fresh })
            setOfferImport(false)
          }}
          className="mt-4 text-sm text-ink/70 hover:text-ink underline"
        >
          Start fresh instead
        </button>
      </Notice>
    )
  }

  if (!current) return <Splash label="Loading your plan…" />

  return (
    <AppStateProvider initial={current} onChange={doc.push}>
      <AppContent sync={doc} />
    </AppStateProvider>
  )
}

function Gate() {
  const { status, refresh } = useAuth()

  if (status === 'checking') return <Splash />

  if (status === 'unreachable') {
    return (
      <Notice title="Can't reach the server" action={{ label: 'Try again', onClick: () => void refresh() }}>
        <p>
          The app is running but the server behind it isn't answering. If you're offline, this will
          sort itself out when you reconnect.
        </p>
      </Notice>
    )
  }

  if (status === 'signed-out') return <AuthScreen />

  return <SignedInApp />
}

/** True on /admin, which has its own sign-in for admin accounts (SM-20), apart from the app's. */
function isAdminPage() {
  return window.location.pathname.replace(/\/+$/, '') === '/admin'
}

/** The privacy policy (/privacy) or terms of use (/terms), readable signed in or out. */
function legalPage() {
  const path = window.location.pathname.replace(/\/+$/, '')
  if (path === '/privacy') return { label: 'The privacy policy', Page: PrivacyPolicy }
  if (path === '/terms') return { label: 'The terms of use', Page: TermsOfUse }
  return null
}

export default function App() {
  // Readable signed in or out, and without waiting on the server to say which:
  // they have to work for someone deciding whether to sign up.
  const legal = legalPage()
  if (legal) {
    return (
      <>
        <ErrorBoundary label={legal.label}>
          <legal.Page />
        </ErrorBoundary>
        <CookieBanner />
      </>
    )
  }

  // Not the app at all: whoever is signed in to the app, the panel asks for an
  // admin account of its own.
  if (isAdminPage()) {
    return (
      <ErrorBoundary label="The admin panel">
        <Suspense fallback={<ChartScreenFallback />}>
          <AdminPanel />
        </Suspense>
      </ErrorBoundary>
    )
  }

  // Outermost backstop: catches anything the inner boundaries don't, including
  // a failure while loading state, so the worst case is a message rather than
  // a blank page.
  return (
    <ErrorBoundary label="Snapshot Money">
      <AuthProvider>
        <SplashHold>
          <Gate />
        </SplashHold>
      </AuthProvider>
      <CookieBanner />
    </ErrorBoundary>
  )
}
